<template>
	<el-dialog
		title="批量设置折旧参数"
		:visible.sync="dialogVisible"
		width="40%"
		top="10vh"
		:close-on-click-modal="false"
		:before-close="handleClose"
		custom-class="depreciation-param-dialog">
		<div class="msg">
			<div class="msg-title">已选择 <span class="count">{{ assetIds.length }}</span> 个资产</div>
			<div class="msg-sub">以下参数将统一应用到所选资产</div>
		</div>
		<el-form ref="form" :model="form" :rules="rules" label-position="top" size="mini">
			<el-row :gutter="20">
				<!-- 折旧方法 -->
				<el-col :span="12">
					<el-form-item label="折旧方法" prop="depreciationMethod">
						<el-select
							v-model="form.depreciationMethod"
							size="small"
							placeholder="请选择折旧方法"
							style="width: 100%"
						>
							<el-option label="年限平均法" value="STRAIGHT_LINE" />
							<el-option label="双倍余额递减法" value="DOUBLE_DECLINING_BALANCE" />
							<el-option label="年数总和法" value="SUM_OF_YEARS_DIGITS" />
							<el-option label="工作量法" value="WORKLOAD" />
							<el-option label="未定义" value="UNDEFINED" />
						</el-select>
					</el-form-item>
				</el-col>

				<!-- 预计使用年限 -->
				<el-col :span="12">
					<el-form-item label="预计使用年限" prop="depreciationYears">
						<el-input
							v-model.number="form.depreciationYears"
							size="small"
							type="number"
							placeholder="请输入使用年限（年）"
							class="no-spinner"
						/>
					</el-form-item>
				</el-col>

				<!-- 预计净残值率 -->
				<el-col :span="12">
					<el-form-item label="预计净残值率" prop="residualValueRate">
						<el-input
							v-model.number="form.residualValueRate"
							size="small"
							type="number"
							placeholder="请输入净残值率（%）"
							class="no-spinner"
						/>
					</el-form-item>
				</el-col>

				<!-- 开始计提日期 -->
				<el-col :span="12">
					<el-form-item label="开始计提日期" prop="depreciationStartDate">
						<el-date-picker
							v-model="form.depreciationStartDate"
							size="small"
							type="date"
							placeholder="Select date"
							style="width: 100%"
						/>
					</el-form-item>
				</el-col>

				<!-- 备注说明 -->
				<el-col :span="24">
					<el-form-item label="备注说明" prop="remark">
						<el-input
							type="textarea"
							v-model="form.remark"
							size="small"
							placeholder="请输入备注说明"
							:rows="4"
						/>
					</el-form-item>
				</el-col>
			</el-row>
		</el-form>

		<!-- 按钮 -->
		<span slot="footer">
			<el-button @click="handleClose">取 消</el-button>
			<el-button type="primary" :loading="submitting" @click="handleSubmit">确 定</el-button>
		</span>
	</el-dialog>
</template>

<script>
	import { batchSetDepreciationParam } from '@/api/asset/assetLedgerApi';
	export default {
		name: 'AssetDepreciationBatchParamDialog',
		props: {
			dialogVisible: Boolean,
			assetIds: {
				type: Array,
				default: () => [],
			},
		},
		watch: {
			dialogVisible(val) {
				if (val) {
					this.submitting = false;
					this.$nextTick(() => {
						this.$refs.form && this.$refs.form.resetFields();
						this.form.depreciationStartDate = new Date();
					});
				}
			},
		},
		data() {
			return {
				submitting: false,
				form: {
					depreciationMethod: '',
					depreciationYears: '',
					residualValueRate: '',
					depreciationStartDate: '',
					remark: '',
				},
				rules: {
					depreciationMethod: [
						{ required: true, message: '请选择折旧方法', trigger: 'change' },
					],
					depreciationYears: [
						{ required: true, message: '请输入预计使用年限', trigger: 'blur' },
						{ type: 'number', min: 1, message: '预计使用年限必须大于0', trigger: 'blur' },
					],
					depreciationStartDate: [
						{ required: true, message: '请选择开始计提日期', trigger: 'change' },
					],
				},
			};
		},
		methods: {
			handleClose() {
				this.$emit('update:dialogVisible', false);
			},
			handleSubmit() {
				this.$refs.form.validate(valid => {
					if (!valid) return;
					const paramVO = {
						assetIds: this.assetIds,
						depreciationMethod: this.form.depreciationMethod,
						depreciationYears: this.form.depreciationYears,
						depreciationStartDate: this.form.depreciationStartDate,
						residualValueRate: this.form.residualValueRate,
						remark: this.form.remark,
					};
					this.submitting = true;
					batchSetDepreciationParam(paramVO)
						.then(() => {
							this.$message.success('批量设置折旧参数成功');
							this.$emit('updateSuccess');
							this.handleClose();
						})
						.catch(err => {
							console.log(err);
							this.$message.error(err.message || '批量设置折旧参数失败');
						})
						.finally(() => {
							this.submitting = false;
						});
				});
			},
		},
	};
</script>

<style scoped lang="scss">
	:deep(.depreciation-param-dialog) {
		.no-spinner .el-input__inner::-webkit-outer-spin-button,
		.no-spinner .el-input__inner::-webkit-inner-spin-button {
			-webkit-appearance: none;
			margin: 0;
		}

		.no-spinner .el-input__inner[type='number'] {
			-moz-appearance: textfield; /* Firefox */
		}

		.el-dialog__body {
			max-height: 60vh;
			overflow-y: auto;
		}

		.el-dialog__body::-webkit-scrollbar {
			width: 6px;
		}

		.el-dialog__body::-webkit-scrollbar-track {
			background: #f5f5f5;
			border-radius: 3px;
		}

		.el-dialog__body::-webkit-scrollbar-thumb {
			background: #bbbbbb;
			border-radius: 3px;
			transition: all 0.3s;
		}

		.el-dialog__body::-webkit-scrollbar-thumb:hover {
			background: #a8a8a8;
		}
	}

	.msg {
		background: #f4f4f5;
		padding: 10px 12px;
		border-radius: 4px;
		margin-bottom: 16px;
		color: #333;
	}
	.msg-title {
		font-size: 14px;
		font-weight: 500;
	}
	.msg-title .count {
		color: #409eff;
		font-weight: 600;
	}
	.msg-sub {
		font-size: 12px;
		color: #909399;
		margin-top: 4px;
	}
</style>
